import React from 'react'
import { useTranslation } from 'react-i18next'
import Paper from '@material-ui/core/Paper'
import Grid from '@material-ui/core/Grid'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import CloudDownloadIcon from '@material-ui/icons/CloudDownload'
import ContactSupportIcon from '@material-ui/icons/ContactSupport'
import CircularProgress from '@material-ui/core/CircularProgress'
import { format } from 'date-fns'
import { isWidthUp } from '@material-ui/core/withWidth'

import CenteredProgress from '../../../../utils/centeredprogress'
import TotalPlants from './TotalPlants'
import ClassifyPlants from './ClassifyPlants'
import { BasicStatsParcel } from './BasicStatsParcel'
import { TreeMapLayer } from './TreeMapLayer'
import LegendFilter from './LegendFilter'
import { KeyboardDatePicker } from '@material-ui/pickers'
import { LayerSelector } from '../LayerSelector'
import { MaxCloudCoverageSelector } from '../MaxCloudCoverageSelector'
import { useAuth } from '../../../../context/AuthContext'
import { useSaveCrownsMutation } from '@graniot/common/adapters/plants'
import { usePaletteLegend } from '../../../../adapters/paletteRange'
import { usePaletteAdjustmentsEnabled } from '../../../../context/PaletteAdjustmentsContext'


const units = ['mean', 'max', 'min', 'area']

const ShowPlants = ({
	isLoadingFIS,
	isLoadingPlants,
	farms,
	parcel,
	layers,
	updateLayer,
	dataPlants,
	featureStats,
	startVegetation,
	handleChangeStartVegetation,
	fromDate,
	imagesDate,
	selectedResolution,
	maxCC,
	handleCloudCoverageChange,
	refetchPlants,
	indexFilter,
	setIndexFilter,
	getCustomLayerScript,
	classes,
	width,
	handleRequestTreeAnalysis,
	zones,
	setZonedGeom,
	setSliderChange,
	setStatusSnackbar,
	setMessageSnackbar,
	setOpenSnackbar,
}) => {
	const { t } = useTranslation()
	const { loggedUser, isClientPaying } = useAuth()
	const [statisticUnit, setStatisticUnit] = React.useState(units[0])
	const [statisticGroups, setStatisticGroups] = React.useState(5)

	const [totalPlants, setTotalPlants] = React.useState(null)
	const [selectedLayer, setSelectedLayer] = React.useState(layers.find(item => item.resolution != null))
	const [featureData, setFeatureData] = React.useState(null)
	const [dataMap, setDataMap] = React.useState(null)
	const [plotData, setPlotData] = React.useState(null)

	// Plants, the classification chart and the filter slider use the user's saved palette
	// range for this farm/layer (only in apps that enable palette adjustments)
	const paletteEnabled = usePaletteAdjustmentsEnabled()
	const { legend: plantLegend } = usePaletteLegend(parcel?.id, selectedLayer, { enabled: paletteEnabled })

	const mutateSaveCrowns = useSaveCrownsMutation()

	const handleSaveCrowns = () => {
		mutateSaveCrowns.mutate(
			[selectedLayer.id, parcel.id, indexFilter, startVegetation.date],
			{
				onSuccess: async (data) => {
					setStatusSnackbar('success')
					setMessageSnackbar(t('Crown_Saved_Successfully'))
					setOpenSnackbar(true)
					refetchPlants()
				},
				onError: async (data) => { },
				onSettled: async (data) => { },
			}
		)
	}

	const handleSelectedLayer = (event, newValue) => {
		setSelectedLayer(newValue)
		updateLayer(newValue)
	}

	const disableNotAvalibleSentinelImages = (date) => {
		return indexOfDate(date) === -1
	}

	const indexOfDate = (date) => {
		return featureStats
			.map((d) => format(new Date(d.date), 'dd/MM/yyyy'))
			.indexOf(date)
	}

	const download = (content, fileName, contentType) => {
		var a = document.createElement('a')
		var file = new Blob(
			[JSON.stringify({ type: 'FeatureCollection', features: content })],
			{ type: contentType }
		)
		a.href = URL.createObjectURL(file)
		a.download = fileName
		a.click()
	}

	function split(left, right, parts) {
		var result = [],
			delta = (right - left) / (parts - 1)
		while (left < right) {
			result.push(left)
			left += delta
		}
		result.push(right)
		return result
	}

	const generateCustomAreaColor = (index, totalBuckets) => {
		const hue = (360 / totalBuckets) * index; // Spread hues evenly across the color wheel
		return `hsl(${hue}, 70%, 50%)`; // Vibrant colors with 70% saturation and 50% lightness
	};
	
	const getClosestLegendColor = (value, legend) => {
		let closest = legend.reduce((prev, curr) => {
			return Math.abs(parseFloat(curr.label) - value) <
				Math.abs(parseFloat(prev.label) - value)
				? curr
				: prev;
		});
		return closest.color || '#000000'; // Fallback to black if no color found
	};

	const groupBy = (arr, key, N) => {
		let features = [];

		// Ensure all items have the key in properties
		if (!arr.every(item => key in item.properties)) {
			return features;
		}

		// Calculate min and max using reduce
		let { min, max } = arr.reduce(
			(acc, item) => {
				let value = item.properties[key];
				acc.min = Math.min(acc.min, value);
				acc.max = Math.max(acc.max, value);
				return acc;
			},
			{ min: Infinity, max: -Infinity }
		);

		min = Math.round(min * 1000) / 1000;
		max = Math.round(max * 1000) / 1000;

		// Generate grouping steps
		let steps = split(min, max, N);
		let avg = (max - min) / 2;

		if (avg) {
			// Custom color palette for 'area'
			if (key === 'area') {
				features = steps.map((step, i) => ({
					count: 0,
					index: Math.round(step * 1000) / 1000,
					indexColor: generateCustomAreaColor(i, N),
				}));
			} else {
				// Use colors from legend for other keys
				if (!plantLegend || plantLegend.length === 0) {
					throw new Error('Invalid selectedLayer.legend: Legend must have at least one color entry');
				}
				features = steps.map((step) => ({
					count: 0,
					index: Math.round(step * 1000) / 1000,
					indexColor: getClosestLegendColor(step, plantLegend),
				}));
			}
	
			// Assign items to the closest group and update color
			arr.forEach(item => {
				let value = item.properties[key];
				let closestGroup = features.reduce((prev, curr) => 
					Math.abs(curr.index - value) < Math.abs(prev.index - value) ? curr : prev
				);
				closestGroup.count++;
				item.properties.color = closestGroup.indexColor;
			});
		} else {
			// Handle edge case when min and max are equal
			let color = key === 'area'
				? generateCustomAreaColor(0, 1) // Single custom color for area
				: getClosestLegendColor(min, plantLegend); // Legend color for other keys
	
			features.push({
				count: arr.length,
				index: Math.round(arr[0].properties[key] * 1000) / 1000,
				indexColor: color,
			});
		}
	
		return features;
	};

	React.useEffect(() => {
		if (dataPlants?.features?.length) {
			setPlotData(
				groupBy(dataPlants.features, statisticUnit, statisticGroups)
			)
			setDataMap({ ...dataPlants })
		}
	}, [dataPlants, statisticUnit, statisticGroups, plantLegend])

	React.useEffect(() => {
		setFeatureData(null)
	}, [selectedLayer])


	return (
		<>
			<Grid item xs={12} sm={5}>
				<Grid
					container
					direction="row"
					spacing={1}
				>
					<Grid item xs={12}>
						{totalPlants ? <TotalPlants totalPlants={totalPlants} /> : <CenteredProgress />}
					</Grid>
					<Grid item xs={12}>
						<Grid container spacing={1} alignItems="center">
							<Grid item xs={12} sm={6}>
								<LayerSelector
									layers={layers.filter(layer =>
										!layer.name.endsWith('WMS') &&
										!layer.name.startsWith('NATURA_COLOR') &&
										!layer.name.startsWith('NATURAL_COLOUR') &&
										!layer.name.startsWith('NATURA-COLOR') &&
										!layer.name.startsWith('NATURAL-COLOUR')
									)}
									selectedLayer={selectedLayer}
									handleSelectedLayer={handleSelectedLayer}
									width={'100%'}
								/>
							</Grid>
							<Grid item xs={12} sm={6}>
								{startVegetation && (
									!isLoadingFIS ? (
										<KeyboardDatePicker
											name="base_date"
											label={t('Analysis date')}
											value={startVegetation.date}
											onChange={(date) =>
												handleChangeStartVegetation(
													format(date, 'dd/MM/yyyy')
												)
											}
											shouldDisableDate={(date) =>
												disableNotAvalibleSentinelImages(
													format(date, 'dd/MM/yyyy')
												)
											}
											maxDate={new Date()}
											minDate={new Date(fromDate)}
											format="dd/MM/yyyy"
											variant="inline"
											size="small"
											fullWidth
											autoOk
											margin="dense"
										/>
									) : <CenteredProgress />
								)}
							</Grid>
						</Grid>
					</Grid>
					{selectedLayer.is_sentinel &&
						['10x10 meters', '3x3 meters'].includes(selectedResolution.resolution) && (
							<Grid item xs={12}>
								<MaxCloudCoverageSelector
									maxCC={maxCC}
									handleCloudCoverageChange={
										handleCloudCoverageChange
									}
									info={true}
									width={'100%'}
									disabled={!featureStats?.every(item => item.hasOwnProperty('cloud_coverage'))}
								/>
							</Grid>
						)}

					<Grid item xs={12}>
						{featureData ? (
							<BasicStatsParcel
								basicStats={featureData}
								selectedLayer={selectedLayer}
								refetch={refetchPlants}
								isLoadingPlants={isLoadingPlants}
								setFeatureData={setFeatureData}
							/>
						) : dataPlants ? (
							selectedLayer.name !==
							selectedLayer.layer_stats && (
								<ClassifyPlants
									plotData={plotData}
									units={units}
									statisticUnit={statisticUnit}
									setStatisticUnit={setStatisticUnit}
									statisticGroups={statisticGroups}
									setStatisticGroups={setStatisticGroups}
								/>
							)
						) : (
							<CenteredProgress />
						)}
					</Grid>
				</Grid>
			</Grid>

			<Grid item xs={12} sm={7}>
				<Paper>
					<Box style={{ position: 'relative' }}>
						<TreeMapLayer
							parcel={parcel}
							imagesDate={imagesDate}
							layer={selectedLayer}
							selectedResolution={selectedResolution}
							zones={zones}
							setZonedGeom={setZonedGeom}
							maxCC={maxCC}
							startVegetation={startVegetation}
							setTotalPlants={setTotalPlants}
							data={dataMap}
							refetch={refetchPlants}
							statisticUnit={statisticUnit}
							featureData={featureData}
							setFeatureData={setFeatureData}
							getCustomLayerScript={getCustomLayerScript}
							indexFilter={indexFilter}
							isLoadingFIS={isLoadingFIS}
						/>
						{selectedLayer?.legend?.length > 0 && (
							<Box
								style={{
									position: 'absolute',
									bottom: 12,
									right: 12,
									zIndex: 1000,
									pointerEvents: 'none',
								}}
							>
								<LegendFilter
									legend={plantLegend}
									value={indexFilter}
									onChange={(value) => {
										setSliderChange(true)
										setIndexFilter(value)
									}}
								/>
							</Box>
						)}
					</Box>
				</Paper>
				{(isClientPaying() || loggedUser.is_superuser) &&
					selectedLayer.name !== selectedLayer.layer_stats && (
						<Box marginTop={2}>
							<Box
								marginTop={2}
								display="flex"
								justifyContent={
									isWidthUp('sm', width) ? 'end' : 'center'
								}
							>
								<Button
									id="download-plants-stats"
									variant="outlined"
									color="primary"
									style={{ width: '100%' }}
									onClick={() =>
										download(
											dataPlants.features,
											`${parcel.properties.name}-${t(
												selectedLayer.displayed_name
											)}-${startVegetation.date}.geojson`,
											'application/json'
										)
									}
									disabled={!dataPlants}
									endIcon={<CloudDownloadIcon />}
								>
									{t('Download file')}
								</Button>
							</Box>
							{/* <Box
								marginTop={2}
								display="flex"
								justifyContent={
									isWidthUp('sm', width) ? 'end' : 'center'
								}
							>
								<Button
									id="download-plants-stats"
									variant="outlined"
									color="primary"
									style={{ width: '100%' }}
									disabled={!dataPlants}
									endIcon={<ContactSupportIcon />}
									onClick={handleRequestTreeAnalysis}
								>
									{t('repeat_plants_analysis')}
								</Button>
							</Box> */}
							{[3, 4, 5].includes(selectedResolution.id) && (
								<Box
									marginTop={2}
									display="flex"
									justifyContent={
										isWidthUp('sm', width)
											? 'end'
											: 'center'
									}
								>
									<Button
										id="download-plants-stats"
										variant="outlined"
										color="primary"
										style={{ width: '100%' }}
										disabled={!dataPlants}
										startIcon={
											mutateSaveCrowns.isLoading && (
												<CircularProgress
													color="inherit"
													size={20}
												/>
											)
										}
										endIcon={<ContactSupportIcon />}
										onClick={handleSaveCrowns}
									>
										{'Save crowns'}
									</Button>
								</Box>
							)}
						</Box>
					)}
			</Grid>
		</>
	)
}

export default ShowPlants
